import type { ContentSnapshot, PlayerId } from '../../engine/types';
import { botDeckStrategySignature } from '../deck/deck-selector';
import { dominantPlayerStyle, PLAYER_MODEL_HISTORY_LIMIT, type PlayerLearningModel, type PlayerMatchFeatures, type PlayerStyleVector, type StrategyOutcome } from './types';

const STYLE_KEYS = ['aggression', 'cycle', 'beatdown', 'airPressure', 'swarm', 'spellControl', 'defense', 'lanePreference'] as const;

function mergeStyle(previous: PlayerStyleVector, next: PlayerStyleVector, matchCount: number): PlayerStyleVector {
  const window = Math.min(10, matchCount + 1);
  const result = {} as Record<keyof PlayerStyleVector, number>;
  for (const key of STYLE_KEYS) result[key] = Math.round((previous[key] * (window - 1) + next[key]) / window);
  return result;
}

function updateOutcome(previous: StrategyOutcome | undefined, winner: PlayerId | 'draw'): StrategyOutcome {
  const base = previous ?? { wins: 0, losses: 0, draws: 0 };
  return {
    wins: base.wins + (winner === 'bot' ? 1 : 0),
    losses: base.losses + (winner === 'player' ? 1 : 0),
    draws: base.draws + (winner === 'draw' ? 1 : 0),
  };
}

export function trainPlayerLearningModel(model: PlayerLearningModel, features: PlayerMatchFeatures, content: ContentSnapshot): { model: PlayerLearningModel; updated: boolean } {
  if (model.processedMatchIds.includes(features.matchId)) return { model, updated: false };
  const cards = features.botDeck.map((cardId) => content.cards.get(cardId)).filter((card): card is NonNullable<typeof card> => Boolean(card));
  const outcomeKey = `${dominantPlayerStyle(features.style)}::${botDeckStrategySignature(content, cards.map((card) => card.id))}`;
  const strategyOutcomes = { ...model.strategyOutcomes, [outcomeKey]: updateOutcome(model.strategyOutcomes[outcomeKey], features.result.winner) };
  const matchCount = model.matchCount + 1;
  return {
    updated: true,
    model: {
      ...model,
      revision: model.revision + 1,
      matchCount,
      confidence: Math.min(1_000, matchCount * 125),
      style: mergeStyle(model.style, features.style, model.matchCount),
      processedMatchIds: [...model.processedMatchIds, features.matchId].slice(-PLAYER_MODEL_HISTORY_LIMIT),
      recentMatches: [...model.recentMatches, features].slice(-PLAYER_MODEL_HISTORY_LIMIT),
      strategyOutcomes,
    },
  };
}
